/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import {
    Box, Button, Chip, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Iconify } from 'src/components';

/** Was this reply any good?
 *
 *  The only judgement in the whole screen that does not come from the model, so
 *  it has to be cheap enough that somebody reviewing thirty rows actually uses
 *  it: one click records a verdict, and the tags and the comment are there for
 *  the ones worth explaining.
 *
 *  Deliberately not a five-point scale. A middle option is where an ambivalent
 *  reviewer puts everything, and "3/5" tells nobody what to change.
 *
 *  `compact` is the inbox (R1b): the same component, in a 340px popover sitting
 *  under a mode list and a draft box. It shows the two buttons and folds the
 *  tags and the comment away until there is a reason for them. It is a prop
 *  rather than a second component on purpose — the verdict/tag interaction
 *  below is where every subtle bug in this feature has lived, and two copies of
 *  it would drift apart on the first change to either screen.
 */
export default function TurnRating({
    turn, tagOptions = [], onRate, compact = false,
}) {
    const mine = turn?.ratings?.mine || null;
    const others = (turn?.ratings?.all || []).filter((r) => r.id !== mine?.id);

    const [tags, setTags] = useState(mine?.tags || []);
    const [comment, setComment] = useState(mine?.comment || '');

    // Re-seed when a different rating arrives — keyed on the rating id, not its
    // contents, so an operator mid-sentence is never overwritten by a refresh.
    useEffect(() => {
        setTags(mine?.tags || []);
        setComment(mine?.comment || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mine?.id]);

    const verdict = mine?.verdict || null;

    // Folded away in the popover until it is worth the space. Marking a reply
    // Bad opens it without being asked: "bad" on its own is the least useful
    // thing this table can record, and the moment somebody has just decided a
    // reply was wrong is the only moment they can say why.
    const [expanded, setExpanded] = useState(false);
    const detail = !compact
        || expanded
        || verdict === 'BAD'
        || tags.length > 0
        || !!comment;

    // Nothing here is ever disabled while a save is in flight, and that is a
    // fix rather than an oversight. The controls used to be disabled on a
    // "busy" prop, and the comment box saves on blur — so typing a comment and
    // then clicking the other thumb flipped that prop during the focusout,
    // React flushed it before the click landed, and the browser never
    // dispatched the click. One request went out carrying the OLD verdict and
    // the new comment: a GOOD rating whose own free text said the reply was
    // wrong, with no error shown. Picking a second tag quickly failed the same
    // way, just as silently.
    //
    // Every payload carries the whole state, so overlapping saves are
    // last-write-wins, and the caller discards a response it no longer wants.
    const rate = (nextVerdict, nextTags = tags, nextComment = comment) => {
        // A verdict flip must not carry the previous one's failure tags. They
        // are a failure vocabulary and the metrics count them as failure modes,
        // so "wrong tone" surviving onto a GOOD rating tells the aggregation
        // that a reply somebody approved was badly toned. The API refuses the
        // combination as well; this is so a reviewer never sees it refused.
        const cleaned = nextVerdict === 'GOOD' ? [] : nextTags;
        if (nextVerdict === 'GOOD' && tags.length) setTags([]);
        // Open the detail immediately on a thumbs-down rather than waiting for
        // the saved rating to come back and imply it. The moment somebody has
        // just decided a reply was wrong is the only moment they will say why,
        // and a round-trip's delay — or a failed save — spends it. "Bad" with
        // no tag is the least useful row this table can hold.
        if (nextVerdict === 'BAD') setExpanded(true);
        return onRate({
            verdict: nextVerdict,
            tags: cleaned,
            comment: nextComment,
        });
    };

    const toggleTag = (value) => {
        const next = tags.includes(value)
            ? tags.filter((t) => t !== value)
            : [...tags, value];
        setTags(next);
        // A tag IS the failure statement, so applying one says the reply was
        // wrong — on an unrated turn and equally on one somebody had marked
        // good. Making them click Bad first only loses tags; leaving the good
        // verdict in place while a failure tag hangs off it would tell the
        // aggregation that a reply a human approved was badly toned, which is
        // the combination the API refuses outright.
        rate(next.length ? 'BAD' : verdict || 'BAD', next);
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">
                Was this reply right?
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, mb: 1 }}>
                <Tooltip title="Good reply">
                    <Button
                        size="small"
                        variant={verdict === 'GOOD' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => rate(verdict === 'GOOD' ? '' : 'GOOD')}
                        startIcon={<Iconify icon="solar:like-bold" />}
                    >
                        Good
                    </Button>
                </Tooltip>
                <Tooltip title="Bad reply — tag what went wrong">
                    <Button
                        size="small"
                        variant={verdict === 'BAD' ? 'contained' : 'outlined'}
                        color="error"
                        onClick={() => rate(verdict === 'BAD' ? '' : 'BAD')}
                        startIcon={<Iconify icon="solar:dislike-bold" />}
                    >
                        Bad
                    </Button>
                </Tooltip>
                {verdict && (
                    <Typography variant="caption" color="text.secondary">
                        Saved. Click again to undo.
                    </Typography>
                )}
            </Stack>

            {!detail && (
                <Button
                    size="small"
                    color="inherit"
                    onClick={() => setExpanded(true)}
                    sx={{ px: 0.5, minWidth: 0, fontWeight: 400 }}
                >
                    Say more
                </Button>
            )}

            {detail && (
                <>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {tagOptions.map((option) => (
                            <Chip
                                key={option.value}
                                size="small"
                                label={option.label}
                                color={tags.includes(option.value) ? 'error' : 'default'}
                                variant={tags.includes(option.value) ? 'filled' : 'outlined'}
                                onClick={() => toggleTag(option.value)}
                            />
                        ))}
                    </Stack>

                    <TextField
                        fullWidth
                        multiline
                        size="small"
                        minRows={2}
                        sx={{ mt: 1.5 }}
                        label="Anything worth saying in words (optional)"
                        placeholder="What should it have said instead?"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        onBlur={() => {
                            if (!verdict || comment === (mine?.comment || '')) return;
                            rate(verdict, tags, comment);
                        }}
                        helperText={
                            verdict
                                ? 'Saved when you click away. Read by a person, never counted — the tags are what add up.'
                                : 'Pick Good or Bad first.'
                        }
                    />
                </>
            )}

            {/* Not shown in the popover, and the inbox payload deliberately does
                not carry it. Telling an operator that a colleague already called
                this reply good is the nudge that stops them calling it bad, and
                independent verdicts are the only reason this table is worth
                anything. On the review screen the disagreement IS the thing
                being read, so it stays. */}
            {others.length > 0 && !compact && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Also rated by
                    </Typography>
                    {others.map((r) => (
                        <Typography key={r.id} variant="caption" display="block">
                            <Box
                                component="span"
                                sx={{ color: r.verdict === 'GOOD' ? 'success.main' : 'error.main' }}
                            >
                                {r.verdict === 'GOOD' ? 'Good' : 'Bad'}
                            </Box>
                            {` — ${r.rater_name || 'someone'}`}
                            {r.tags?.length ? ` · ${r.tags.map((t) => t.replace(/_/g, ' ')).join(', ')}` : ''}
                            {r.comment ? ` · “${r.comment}”` : ''}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}
